import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { Email, Manufacturer, PoHeader } from "app/model/entities/entity-model";
import { environment } from "environments/environment";
import { EmailService } from "app/shared/email.service";
import { PoStatusCode } from "app/model/enums/po-status-code";
import { UtilFns } from "app/shared/util-fns";
import {
  DialogBaseComponent,
  DialogParams,
} from "app/shared/dialog-base.component";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { PoUtilsService } from "@/shared/po-utils.service";
import { wrapBusy } from "@/shared/domain-base.component";

export interface PoPrintDialogParams extends DialogParams {
  poHeader: PoHeader;
  /** if we are not already on the preview page */
  canPreview: boolean;
}

@Component({
  templateUrl: "./po-print-dialog.component.html",
  standalone: false,
})
export class PoPrintDialogComponent extends DialogBaseComponent<PoPrintDialogParams> {
  poHeader: PoHeader;
  mfg: Manufacturer;
  emailAddress: string;
  email: Email;
  pdfurl: string;
  canPreview: boolean;
  isPrinting: boolean;
  isOpenIncomplete: boolean;
  isEmailed = false;

  constructor(
    public config: DynamicDialogConfig<PoPrintDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private poUtilsService: PoUtilsService,
    private emailService: EmailService,
    private router: Router
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "800px",
      height: "340px",
    });
    this.prepare();
  }

  async prepare() {
    this.poHeader = this.params.poHeader;
    this.canPreview = this.params.canPreview;

    this.config.header = "Print/Email Purchase Order";

    this.mfg = this.poHeader.manufacturer;
    // this.emailAddress = this.mfg.whEmail || this.mfg.billingEmail;
    this.emailAddress = this.mfg.whEmail;
    if (this.emailAddress) {
      this.emailAddress = this.emailAddress.trim();
    }

    this.pdfurl =
      environment.apiRoot +
      "api/pdf/pdffromurl/PO" +
      this.poHeader.id +
      "?url=" +
      UtilFns.getBaseUrl() +
      "printable-po/" +
      this.poHeader.id +
      "&headerLeft=Purchase Order " +
      this.poHeader.id;
    await this.getEmailStatus(false);
    this.isPageReady = true;
  }

  canSendEmail(): boolean {
    if (this.isEmailed) return false;

    if (this.poHeader.poStatusId === PoStatusCode.Complete) {
      return false;
    }
    return UtilFns.validateMultipleEmail(this.emailAddress);
  }

  @wrapBusy
  async sendEmail() {
    if (!this.canSendEmail()) {
      return;
    }
    this.isEmailed = true;

    const status = await this.emailService.sendPoEmail(
      this.poHeader.id,
      this.emailAddress
    );

    if (status === "accepted" || status === "stored") {

      this.toastr.info(status + "; checking for delivery", "Email Status");
      await this.poUtilsService.markPoSubmitted(this.poHeader);
      setTimeout(() => this.getEmailStatus(), 3000);
    } else {
      this.isEmailed = false;
      this.toastr.error(status, "Email Status");
    }

  }

  async getEmailStatus(showToast = true) {
    const email = await this.emailService.getPoEmailStatus(this.poHeader.id);
    if (email) {
      this.email = email;
      if (showToast) {
        this.toastr.info(email.status, "Email Status");
      }
    }
  }

  printDocument() {
    const pwin = window.open(this.pdfurl, "_blank");
    if (this.poHeader.poStatusId === PoStatusCode.NewUnposted) {
      setTimeout(() => (this.isPrinting = true), 1000);
    }
  }

  @wrapBusy
  async setStatusOpen() {
    try {
      await this.poUtilsService.markPoSubmitted(this.poHeader);

      this.isOpenIncomplete = true;
    } finally {
      this.isPrinting = false;
    }
  }

  showPreview() {
    this.router.navigateByUrl("/printable-po/" + this.poHeader.id);
    this.close();
  }

  close() {
    this.dialogRef.close(null);
  }
}
